import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';

import { getLanguageFromLocale, noticeError } from '@ratehub/base-ui';

import CARD_COMPARISON_DEFAULTS from '../definitions/CardComparisonDefaults';
import DEFAULT_CARD_PREFERENCES from '../definitions/DefaultCardPreferences';


function useCreditCardComparison() {
    const intl = useIntl();
    const [ creditCardComparison, setCreditCardComparison ] = useState(getInitialState);

    // Subscribe to changes to local storage; provides reactivity if anything updates the stored comparison list
    useEffect(() => {
        function handleStorageChange(event) {
            if (event.key === LOCAL_STORAGE_KEY) {
                const updatedCreditCardComparison = event.newValue
                    ? JSON.parse(event.newValue)
                    : INITIAL_COMPARISON_VALUES;

                setCreditCardComparison(updatedCreditCardComparison);
            }
        }

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);


    function getInitialState() {
        if (typeof localStorage === 'undefined') {
            return INITIAL_COMPARISON_VALUES;
        }

        const currentCreditCardComparison = localStorage.getItem(LOCAL_STORAGE_KEY);

        return currentCreditCardComparison
            ? JSON.parse(currentCreditCardComparison)
            : INITIAL_COMPARISON_VALUES;
    }

    function updateCreditCardComparison(ccComparison) {
        const ccComparisonAsJSON = JSON.stringify(ccComparison);

        localStorage.setItem(LOCAL_STORAGE_KEY, ccComparisonAsJSON);

        // Manually dispatch a storage event to notify current window
        // as storage events are not normally dispatched in the same window
        window.dispatchEvent(new StorageEvent('storage', {
            key: LOCAL_STORAGE_KEY,
            newValue: ccComparisonAsJSON,
        }));

        setCreditCardComparison(ccComparison);
    }

    function replaceAllCards(cardSummaries) {
        const updatedSelectedCards = cardSummaries
            .filter(card => card && card.id) // Filter out nulls
            .slice(0, CARD_COMPARISON_DEFAULTS.maxComparisonCardCount)
            .map(card => ({
                id: card.id,
                name: card?.name,
                cardImageURL: card?.cardImageURL,
            }));

        updateCreditCardComparison({
            ...creditCardComparison,
            timestamp: dayjs().toISOString(),
            selectedCards: updatedSelectedCards,
        });
    }

    function addCard(theCard) {
        if (!theCard) {
            noticeError('[addCard] Attempted to add an empty card to the comparison list', {
                existingCards: creditCardComparison.selectedCards,
                theCard,
            });

            return;
        }

        const { id, cardImageURL, name } = theCard;

        if (!id || !name || !cardImageURL) {
            // Ensure that the card has all necessary information before adding
            // This prevents issues with incomplete data in the comparison list
            noticeError('[addCard] Attempted to add a card without valid information', {
                cardId: id,
                cardName: name,
                cardImageURL,
                existingCards: creditCardComparison.selectedCards,
            });

            return;
        }

        const cardExists = creditCardComparison.selectedCards.some(card => card.id === id);

        if (cardExists) {
            noticeError('[addCard] Attempted to add a duplicate card to the comparison list', {
                cardId: id,
                cardName: name,
                existingCards: creditCardComparison.selectedCards,
            });

            return; // Prevent adding duplicates
        }

        if (creditCardComparison.selectedCards.length < CARD_COMPARISON_DEFAULTS.maxComparisonCardCount) {
            updateCreditCardComparison({
                ...creditCardComparison, // Preserve other properties
                timestamp: dayjs().toISOString(),
                selectedCards: [
                    ...creditCardComparison.selectedCards,
                    {
                        id,
                        name,
                        cardImageURL,
                        language: getLanguageFromLocale(intl.locale),
                    },
                ],
            });
        } else {
            noticeError('[addCard] Tried to add a card to the comparison list when it is already full', {
                cardId: id,
                cardName: name,
                existingCards: creditCardComparison.selectedCards,
            });
        }
    }

    function removeCard(id) {
        const cardExists = creditCardComparison.selectedCards.some(card => card.id === id);

        if (!cardExists) {
            noticeError('[removeCard] Attempted to remove a card that is not in the comparison list', {
                cardId: id,
                existingCards: creditCardComparison.selectedCards,
            });

            return; // Prevent removing a card that doesn't exist
        }

        const updatedSelectedCards = creditCardComparison.selectedCards.filter(card => card.id !== id);

        updateCreditCardComparison({
            ...creditCardComparison, // Preserve other properties
            selectedCards: updatedSelectedCards,
        });
    }

    function clearAllCards() {
        updateCreditCardComparison({
            ...creditCardComparison, // Preserve other properties
            selectedCards: [],
        });
    }

    function updateMonthlySpending(newMonthlySpending, newMonthlySpendingAverage) {
        updateCreditCardComparison({
            ...creditCardComparison, // Preserve other properties
            monthlySpending: newMonthlySpending,
            monthlySpendingAverage: newMonthlySpendingAverage,
        });
    }

    return {
        addCard,
        removeCard,
        clearAllCards,
        replaceAllCards,
        creditCardComparison,
        updateMonthlySpending,
    };
}


const LOCAL_STORAGE_KEY = 'cc-card-comparison';

const INITIAL_COMPARISON_VALUES = {
    selectedCards: [],
    monthlySpending: DEFAULT_CARD_PREFERENCES.monthlySpending,
    monthlySpendingAverage: DEFAULT_CARD_PREFERENCES.monthlySpendingAverage,
    timestamp: null,
};


export default useCreditCardComparison;
